import React from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from '~/i18n'
import styled from '@emotion/styled'
import { useRouter } from 'next/router'
import { parse } from 'querystringify'
import { useQuery, useMutation } from '@apollo/client'
import { space, radius, color, H4, Text, BaseButton, device, Toggle } from '@ticketswap/solar'
import { Alert } from '@ticketswap/comets'
import { GET_EVENT_DATA } from '~/graphql/queries/getEventData'
import { GET_ACTIVE_TICKET_ALERTS } from './getActiveTicketAlerts'
import { CHANGE_TICKET_ALERTS } from './changeTicketAlerts'
import TicketAlertsDialog from './TicketAlertsDialog'
import PriceLimitDialog from './PriceLimitDialog'
import AppsAdoptionDialog from '../LegacyAppsAdoptionDialog'
import AppsDevicesDialog from '../LegacyAppsDevicesDialog'
import AuthDialog from '../AuthDialog'
import { useViewer } from '../ViewerContext'
import { track } from '~/utils/analytics'
import { AUTHENTICATION_VIEWED } from '~/lib/tracking-events'
import { formatMoney } from '~/utils/money'

const Container = styled.div`
  border-radius: ${radius.lg};
  padding: ${space[16]};
  border: 1px solid ${color.spaceLightest};
  display: flex;
  justify-content: space-between;

  @media ${device.tablet} {
    padding: ${space[24]};
  }
`

const Description = styled(Text)`
  color: ${color.spaceMedium};
  margin-bottom: 2px; /* optical alignment */

  @media ${device.tablet} {
    margin: 0;
  }
`

const StyledBaseButton = styled(BaseButton)`
  margin-top: ${space[4]};
`

function TicketAlertsManager({ t, ...props }) {
  const router = useRouter()
  const { viewer } = useViewer()
  const EVENT_CONTEXT = typeof props.eventTypeId === 'undefined'
  const EVENT_TYPE_CONTEXT = typeof props.eventTypeId !== 'undefined'
  const [changeTicketAlerts, { loading }] = useMutation(CHANGE_TICKET_ALERTS)
  const { data = {} } = useQuery(GET_ACTIVE_TICKET_ALERTS, { skip: !viewer })
  const { data: eventData = {} } = useQuery(GET_EVENT_DATA, {
    variables: { id: props.eventId },
  })

  /**
   * Determines which label to render for the main action in each
   * situtation.
   */
  function getActionLabel() {
    const alerts = data?.viewer?.activeTicketAlerts || []

    // Filters
    const event = ({ event }) => event.id === props.eventId
    const oneType = ({ event, eventType }) => event.id === props.eventId && eventType
    const eventTypeWithPriceLimit = ({ eventType, maximumPrice }) =>
      eventType?.id === props.eventTypeId && maximumPrice

    // On event-type level, check if existing alerts have a price
    // limit defined or not.
    if (EVENT_TYPE_CONTEXT) {
      if (alerts.filter(eventTypeWithPriceLimit).length > 0) {
        return t('event-eventtype:ticketAlerts.action.editLimit', {
          limit: formatMoney(alerts.filter(eventTypeWithPriceLimit)[0].maximumPrice),
        })
      } else {
        return t('event-eventtype:ticketAlerts.action.setLimit')
      }
    }

    // On event level.
    if (EVENT_CONTEXT) {
      // Check if there’s multiple alerts with the same event ID,
      // which is and indicator for alerts of multiple, but _not all_
      // types of the same event.
      if (alerts.filter(event).length > 1) {
        return t('event-eventtype:ticketAlerts.action.multipleTypes', {
          count: alerts.filter(event).length,
        })
      }

      // Check if there’s an alerts for only _one_ type of the event.
      if (alerts.filter(oneType).length === 1) {
        return alerts.filter(event)[0]?.eventType?.title
      }
    }

    return t('event-eventtype:ticketAlerts.action.allTypes')
  }

  /**
   * Determines whether the toggle is initially on or off in each
   * situation.
   */
  function getOn() {
    const alerts = data?.viewer?.activeTicketAlerts || []

    // Early exit if there’s no alerts at all.
    if (alerts.length === 0) return false

    // Filters
    const event = ({ event }) => event.id === props.eventId
    const eventType = ({ eventType }) => eventType?.id === props.eventTypeId
    const eventWithoutEventType = ({ event, eventType }) => event.id === props.eventId && !eventType

    // On event level, we just need to check whether the array
    // contains the current event ID.
    if (EVENT_CONTEXT && alerts.filter(event).length > 0) {
      return true
    }

    // On event-type level, check if the array contains the current event-type ID.
    if (EVENT_TYPE_CONTEXT && alerts.filter(eventType).length > 0) {
      return true
    }

    // On event-type level, if the current event-type ID is _not_ in
    // the array, check if the event ID is there, but no eventType is
    // defined. This would mean that event-level alerts are enabled.
    if (EVENT_TYPE_CONTEXT && alerts.filter(eventWithoutEventType).length > 0) {
      return true
    }

    return false
  }

  async function handleChangeMutation(payload = []) {
    await changeTicketAlerts({
      variables: {
        input: {
          eventTypesAlert: [...payload],
        },
      },
      update(cache, { data: { changeTicketAlerts } }) {
        const { viewer } = cache.readQuery({ query: GET_ACTIVE_TICKET_ALERTS })
        cache.writeQuery({
          query: GET_ACTIVE_TICKET_ALERTS,
          data: {
            viewer: {
              ...viewer,
              activeTicketAlerts: changeTicketAlerts.ticketAlerts,
            },
          },
        })
      },
    })
  }

  async function toggleAlerts() {
    if (!loading) {
      if (EVENT_CONTEXT) {
        await handleChangeMutation(
          eventData?.node?.types?.edges?.map(({ node }) => ({
            eventTypeId: node.id,
            active: !getOn(),
          }))
        )
      } else if (EVENT_TYPE_CONTEXT) {
        await handleChangeMutation([
          {
            eventTypeId: props.eventTypeId,
            active: !getOn(),
          },
        ])
      }

      props.onToggle(!getOn())
    }
  }

  React.useEffect(() => {
    // Check if the current URL contains `?enableTicketAlerts=true`
    // and enable alerts if it does. This is used for enabling alerts
    // after magic-link auth.
    const { enableTicketAlerts } = parse(router?.asPath.split('?')[1])
    const enableAlertsOnMount = async () => await toggleAlerts()
    if (!getOn() && Boolean(enableTicketAlerts)) {
      enableAlertsOnMount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Container>
      <div>
        <H4>{t('event-eventtype:ticketAlerts.title')}</H4>
        <Description as="p">{t('event-eventtype:ticketAlerts.text')}</Description>
        {getOn() && (
          <>
            {EVENT_CONTEXT ? (
              <TicketAlertsDialog
                t={t}
                alerts={data?.viewer?.activeTicketAlerts}
                eventId={props.eventId}
                types={{
                  entrance: eventData?.node?.entranceTypes?.edges,
                  nonEntrance: eventData?.node?.nonEntranceTypesWithoutGroup?.edges,
                  groups: eventData?.node?.eventTypeGroups,
                }}
                onClose={async payload => {
                  await handleChangeMutation(payload)
                }}
                onToggle={props.onToggle}
              >
                {({ getToggleProps }) => (
                  <StyledBaseButton {...getToggleProps()}>{getActionLabel()}</StyledBaseButton>
                )}
              </TicketAlertsDialog>
            ) : (
              <PriceLimitDialog
                t={t}
                eventId={props.eventId}
                eventTypeId={props.eventTypeId}
                alerts={data?.viewer?.activeTicketAlerts}
                types={eventData?.node?.types?.edges}
                onUpdate={props.onUpdate}
              >
                {({ getToggleProps }) => (
                  <StyledBaseButton {...getToggleProps()}>{getActionLabel()}</StyledBaseButton>
                )}
              </PriceLimitDialog>
            )}
          </>
        )}
      </div>
      <footer>
        <AuthDialog
          onToggle={on => {
            on && track(AUTHENTICATION_VIEWED, { trigger: 'TICKET_ALERT' })
          }}
          emailLoginRedirectUrl={`${global?.location?.href}?enableTicketAlerts=true`}
          onComplete={async () => {
            await toggleAlerts()
          }}
        >
          {({ show: showAuthDialog }) => (
            <AppsDevicesDialog
              t={t}
              eventName={eventData?.node?.name}
              userDevices={viewer?.userDevices}
            >
              {({ show: showAppsDevicesDialog }) => (
                <AppsAdoptionDialog
                  t={t}
                  event={{
                    name: eventData?.node?.name,
                    isPopular: eventData?.node?.isPopular,
                  }}
                >
                  {({ show: showAppsAdoptionDialog }) => (
                    <Toggle
                      adornment={<Alert size={16} />}
                      loading={loading}
                      on={getOn()}
                      onToggle={async on => {
                        if (!viewer) {
                          return showAuthDialog()
                        }

                        await toggleAlerts()

                        // If the viewer does have devices connected
                        // to their account, show the app devices dialog.
                        if (on && viewer?.userDevices?.length) {
                          return showAppsDevicesDialog()
                        }

                        // If the viewer does not have any devices connected
                        // to their account, show the app promo dialog.
                        if (on && viewer?.userDevices?.length === 0) {
                          showAppsAdoptionDialog()
                        }
                      }}
                    />
                  )}
                </AppsAdoptionDialog>
              )}
            </AppsDevicesDialog>
          )}
        </AuthDialog>
      </footer>
    </Container>
  )
}

TicketAlertsManager.defaultProps = {
  onToggle: () => {},
  onUpdate: () => {},
}

TicketAlertsManager.propTypes = {
  eventId: PropTypes.string.isRequired,
  eventTypeId: PropTypes.string,
  onToggle: PropTypes.func,
  onUpdate: PropTypes.func,
}

export default withTranslation(['common', 'event-eventtype'])(TicketAlertsManager)
